import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Mail, MessageCircle, MessageSquare, PhoneCall } from "lucide-react";

import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

// Lives here, not in App.js: App.js already imports from omni.js, so keeping the shared helper on
// this side means one import direction instead of a cycle. useApi does response.text() ->
// JSON.parse and cannot download binary, so every export stays JSON.
export function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);  // revoking synchronously cancels the download in some browsers
}

// App.js doesn't export its useResource/api helpers (only a default export), so this is the
// same thin fetch-hook pattern copied locally rather than restructuring App.js to export it.
// Exported for datenschutz.js (App.js -> datenschutz.js -> omni.js) rather than copied a third time.
export function useOmniResource(api, path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    if (!path) { setLoading(false); return; }
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await api(path);
      if (seq === seqRef.current) setData(result);
    } catch (err) {
      if (seq === seqRef.current) setError(err.message);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, path]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load };
}

// Exported for datenschutz.js (App.js -> datenschutz.js -> omni.js) rather than copied a third time.
export function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

const CHANNEL_META = {
  voice: { label: "Anruf", icon: PhoneCall },
  chat: { label: "Chat", icon: MessageSquare },
  whatsapp: { label: "WhatsApp", icon: MessageCircle },
  email: { label: "E-Mail", icon: Mail },
};

export function ChannelBadge({ channel }) {
  const meta = CHANNEL_META[channel] || { label: "Messenger", icon: MessageCircle };
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className="channel-badge gap-1" data-testid={`channel-badge-${channel || "unknown"}`}>
      <Icon className="h-3.5 w-3.5" /> {meta.label}
    </Badge>
  );
}

export function ContactPanel({ contact, loading }) {
  if (loading) {
    return <Card className="dashboard-card"><CardContent className="p-4"><Skeleton className="h-24" /></CardContent></Card>;
  }
  if (!contact) return null;
  return (
    <Card className="dashboard-card" data-testid="contact-panel">
      <CardHeader><CardTitle className="text-base">Kontakt</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div><span className="text-muted-foreground">Name</span><div className="font-medium">{contact.name || "—"}</div></div>
        <div><span className="text-muted-foreground">Firma</span><div>{contact.company || "—"}</div></div>
        <div><span className="text-muted-foreground">Telefon</span><div>{(contact.phones || []).join(", ") || "—"}</div></div>
        <div><span className="text-muted-foreground">E-Mail</span><div>{(contact.emails || []).join(", ") || "—"}</div></div>
        <div><span className="text-muted-foreground">Zuletzt aktiv</span><div>{formatDateTime(contact.last_seen_at)}</div></div>
      </CardContent>
    </Card>
  );
}

export function ContactsPage({ api, clientId, canGdpr = {} }) {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [erasePreview, setErasePreview] = useState(null);
  const [confirmValue, setConfirmValue] = useState("");
  const [erasing, setErasing] = useState(false);
  const [exportingContact, setExportingContact] = useState(false);

  // Belt to closeSheet's braces: whenever the open contact changes, any preview or typed
  // confirmation from the previous one is dropped. Without this, an in-flight dry-run that
  // resolves after the sheet was closed could re-open the confirm panel over a DIFFERENT
  // contact — showing contact A's counts while the confirm button deletes contact B.
  useEffect(() => { setErasePreview(null); setConfirmValue(""); }, [openId]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = `/contacts?client_id=${clientId}${search ? `&search=${encodeURIComponent(search)}` : ""}`;
  const contacts = useOmniResource(api, query, [clientId, search]);
  const detail = useOmniResource(api, openId ? `/contacts/${openId}` : null, [openId]);

  // Never carry a preview or a typed confirmation from one contact to the next.
  const closeSheet = () => { setOpenId(null); setErasePreview(null); setConfirmValue(""); };

  const exportContact = async () => {
    setExportingContact(true);
    try {
      downloadJson(await api(`/contacts/${openId}/export`), `kontakt-${openId}.json`);
    } catch (err) { toast.error(err.message); } finally { setExportingContact(false); }
  };

  // Step 1 of the two-step erase: dry_run=true is a pure preview, it deletes nothing. The id is
  // captured up front and re-checked on arrival, so a slow response can never paint one contact's
  // deletion counts onto another contact's confirm dialog.
  const previewErase = async () => {
    const targetId = openId;
    setConfirmValue("");
    try {
      const preview = await api(`/contacts/${targetId}?dry_run=true`, { method: "DELETE" });
      // Stamped with the contact it describes; the panel below only renders when that stamp
      // still matches the open contact, so a late response can never be acted on for another one.
      setErasePreview({ ...preview.would_delete, contactId: targetId });
    } catch (err) { toast.error(err.message); }
  };

  // Step 2: the real, irreversible run — aimed at the id the preview was computed for, never at
  // whatever happens to be open now. The typed value must name that contact.
  const confirmErase = async () => {
    const targetId = erasePreview?.contactId;
    if (!targetId) return;
    setErasing(true);
    try {
      const result = await api(`/contacts/${targetId}?dry_run=false&delete_provider=true`, {
        method: "DELETE", body: JSON.stringify({ confirm: confirmValue }),
      });
      if (result.status === "complete") toast.success("Kontakt und Gespräche gelöscht");
      else if (result.status === "nothing_to_do") toast.info("Für diesen Kontakt war nichts mehr zu löschen.");
      else if (result.status === "incomplete") toast.error(result.failures?.length
        ? `Löschung unvollständig — ${result.failures.length} Gespräch(e) konnten nicht gelöscht werden. Bitte erneut versuchen.`
        : `Während der Löschung sind ${result.arrived_conversation_ids?.length || 0} neue Gespräch(e) dieses Kontakts eingegangen. Bitte erneut löschen.`);
      else if (result.status === "local_only") toast.warning(`Lokal gelöscht — bei ${result.skipped_at_provider?.length || 0} Gespräch(en) konnten die Daten beim Anbieter nicht gelöscht werden; dort ist separat zu löschen.`);
      else toast.warning(`Lokal gelöscht — ${result.failures?.length || 0} Löschung(en) beim Anbieter noch nicht bestätigt.`);
      closeSheet();
      contacts.reload();
    } catch (err) { toast.error(err.message); } finally { setErasing(false); }
  };

  return (
    <Card className="dashboard-card">
      <CardHeader><CardTitle>Kontakte</CardTitle><CardDescription>Ansprechpartner aus WhatsApp, E-Mail und weiteren Kanälen.</CardDescription></CardHeader>
      <CardContent>
        <div className="filters-bar">
          <Input className="bg-white" placeholder="Kontakt suchen" data-testid="contacts-search-input" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        {contacts.loading && !contacts.data ? <Skeleton className="h-60" data-testid="state-loading" /> : contacts.data?.length ? (
          <Table data-testid="contacts-table">
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Firma</TableHead><TableHead>Telefon</TableHead><TableHead>E-Mail</TableHead><TableHead>Zuletzt aktiv</TableHead></TableRow></TableHeader>
            <TableBody>
              {contacts.data.map((c) => (
                <TableRow key={c.id} data-testid="contact-row" className="cursor-pointer" role="button" tabIndex={0}
                  onClick={() => setOpenId(c.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(c.id); } }}>
                  <TableCell className="font-medium">{c.name || "—"}</TableCell>
                  <TableCell>{c.company || "—"}</TableCell>
                  <TableCell>{(c.phones || []).join(", ") || "—"}</TableCell>
                  <TableCell>{(c.emails || []).join(", ") || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDateTime(c.last_seen_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <p className="text-sm text-muted-foreground" data-testid="state-empty">Keine Kontakte gefunden.</p>}
      </CardContent>
      <Sheet open={!!openId} onOpenChange={(open) => { if (!open) closeSheet(); }}>
        <SheetContent data-testid="contact-detail-sheet">
          <SheetHeader>
            {/* Always compare against openId: useOmniResource keeps the PREVIOUS contact's data
                while the next one loads (and forever, if that load fails), so rendering it
                unguarded shows one contact's name and phone number above another's actions. */}
            <SheetTitle>{detail.data?.id === openId ? detail.data.name || "Kontakt" : "Kontakt"}</SheetTitle>
            <SheetDescription>Kontaktdaten und verknüpfte Gespräche.</SheetDescription>
          </SheetHeader>
          {detail.loading || detail.data?.id !== openId ? <Skeleton className="h-40 mt-4" /> : detail.data ? (
            <div className="space-y-4 mt-4">
              <ContactPanel contact={detail.data} loading={false} />
              {/* Per-member rights (Art. 15/20 export, Art. 17 erasure), derived in ClientPage —
                  the two are granted independently, so each button is gated on its own flag. */}
              {canGdpr.export || canGdpr.erase ? (
                <div className="space-y-3" data-testid="contact-gdpr-actions">
                  <div className="flex gap-2 flex-wrap">
                    {canGdpr.export ? <Button variant="outline" size="sm" onClick={exportContact} disabled={exportingContact} data-testid="contact-export-button">{exportingContact ? "Export läuft…" : "Export JSON"}</Button> : null}
                    {canGdpr.erase ? <Button variant="destructive" size="sm" onClick={previewErase} data-testid="contact-erase-button">DSGVO löschen</Button> : null}
                  </div>
                  {erasePreview?.contactId === openId ? (
                    <div className="rounded-md border border-destructive/40 p-3 space-y-2 text-sm" data-testid="contact-erase-confirm">
                      <p className="font-medium text-destructive">
                        {detail.data.name || "Kontakt"} endgültig löschen — nicht rückgängig zu machen.
                      </p>
                      <p className="text-muted-foreground">
                        Gelöscht werden: {erasePreview.conversations} Gespräch(e), {erasePreview.messages} Nachricht(en),
                        {" "}{erasePreview.workflow_runs} Workflow-Lauf/-Läufe, {erasePreview.webhook_events} Rohdatensatz/-sätze
                        {" "}sowie die Kontaktdaten. Aufzeichnungen beim
                        Sprachanbieter werden ebenfalls gelöscht; Abrechnungsdaten bleiben aus steuerrechtlichen Gründen erhalten.
                      </p>
                      <p className="text-muted-foreground">
                        Zum Bestätigen eine hinterlegte Rufnummer oder E-Mail-Adresse dieses Kontakts eingeben
                        {(detail.data.phones || []).length || (detail.data.emails || []).length ? "" : <> — oder die Kontakt-ID <span className="font-mono">{openId}</span></>}:
                      </p>
                      <Input className="bg-white" value={confirmValue} onChange={(e) => setConfirmValue(e.target.value)}
                        placeholder="+49… oder name@example.com" data-testid="contact-erase-confirm-input" />
                      <div className="flex gap-2">
                        <Button variant="destructive" size="sm" disabled={erasing || !confirmValue.trim()} onClick={confirmErase} data-testid="contact-erase-submit-button">
                          {erasing ? "Löschen läuft…" : "Endgültig löschen"}
                        </Button>
                        <Button variant="outline" size="sm" disabled={erasing} onClick={() => setErasePreview(null)}>Abbrechen</Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> Gespräche</h4>
                <div className="space-y-2">
                  {(detail.data.conversations || []).length ? detail.data.conversations.map((conv) => (
                    <div key={conv.id} className="flex items-center justify-between gap-2 rounded-md border p-2 text-sm cursor-pointer hover:bg-muted/40"
                      data-testid="contact-conversation-row" role="button" tabIndex={0}
                      onClick={() => navigate(`/clients/${clientId}/conversations/${conv.id}`)}>
                      <ChannelBadge channel={conv.channel} />
                      <span className="text-xs text-muted-foreground">{formatDateTime(conv.activity_at)}</span>
                    </div>
                  )) : <p className="text-sm text-muted-foreground">Keine Gespräche verknüpft.</p>}
                </div>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </Card>
  );
}
